import {
  CATALOG,
  defaultOnOf,
  drivenOptionOf,
  fallbackOf,
  labelOf,
  visibleSettings,
} from "./catalog";
import { EVERY_SCREEN } from "../routes/registry";
import { EVENT_NAME_KEY, eventNameOf } from "./eventName";
import type { CatalogContext, Setting } from "./types";

/**
 * ═══ CHERCHER UN RÉGLAGE DANS TOUT LE PANNEAU ═══
 *
 * Pour trouver un réglage, il fallait savoir sur quel écran il vit, puis dans quelle
 * famille. La recherche répond aux deux à la fois, et accepte deux sortes de mots :
 *
 *   — ceux du PANNEAU : le libellé, la section, la famille (« libellé du bouton ») ;
 *   — ceux du SITE : le texte que le réglage affiche aujourd'hui (« Payer »,
 *     « Retour à l'accueil »). C'est souvent le seul nom que l'organisateur connaît ;
 *   — ses MOTS-CLÉS (`keywords`), quand le mot cherché n'est ni dans l'un ni dans
 *     l'autre : « logo » ne nomme aucun réglage, et trois servent à en poser un.
 *     Pas l'aide entière : ses longues phrases feraient répondre presque tout.
 *
 * Module pur : le panneau lui passe le contexte de l'événement et de quoi lire les
 * valeurs, et décide seul de la navigation.
 */

export type SearchEntry = {
  setting: Setting;
  /** Portée du catalogue : `site`, ou la clé d'un écran. */
  scope: string;
  /** Le nom de l'écran, tel que la barre l'affiche. */
  screen: string;
  /** La famille, telle que le panneau l'affiche. */
  group: string;
  /** La condition de la famille entière (une étape du parcours), s'il y en a une. */
  groupNeeds?: string;
  /** Les autres réglages de la famille : pour savoir si celui-ci est masqué. */
  siblings: ReadonlyArray<Setting>;
  /**
   * La clé de la ligne à désigner. La sienne, sauf pour une ligne portée par un
   * gestionnaire (le nom d'une étape, d'une page) : c'est le gestionnaire qu'on montre.
   */
  aimKey: string;
};

export type Hidden =
  /** La famille entière dort : son étape n'est pas servie. */
  | { by: "step" }
  /**
   * Un interrupteur le masque. `where` dit où il se règle quand il vit dans une AUTRE
   * famille (voir `placeOf`) ; `key` est alors la ligne cherchée elle-même.
   */
  | {
      by: "switch";
      key: string;
      label: string;
      where?: string;
      /** Masqué tant que l'interrupteur est ALLUMÉ (`needs: "!…"`). */
      negated?: true;
    }
  /**
   * Un sélecteur le masque par l'option choisie (le mode de connexion). `key` est la
   * ligne à désigner : le sélecteur s'il est dans la famille, sinon le réglage lui-même.
   */
  | { by: "mode"; key: string; label: string; option: string; where?: string }
  /** Masqué par un autre réglage de la famille. */
  | { by: "other" };

export type SearchHit = SearchEntry & {
  /** Le texte affiché sur le site, quand c'est lui qui a répondu. */
  shown?: string;
  hidden?: Hidden;
};

const MAX_HITS = 50;

export function screenName(scope: string): string {
  if (scope === "site") return "Le site";
  return EVERY_SCREEN.find((s) => s.key === scope)?.label ?? scope;
}

let homes: Map<string, SearchEntry> | undefined;

/**
 * OÙ SE RÈGLE UNE CLÉ, dit comme le panneau l'affiche : « Le site · Pied de page · Le
 * pied de page ». Vide pour une clé que le catalogue ne déclare pas.
 *
 * ⚠️ UNE NOTE « MASQUÉ » QUI NE DIT PAS OÙ N'EXPLIQUE RIEN (bug #294). « Image de pied
 * de page » dort tant que « Afficher le pied de page » est éteint, et cet interrupteur
 * vit dans une AUTRE famille ; l'interrupteur d'une étape s'appelle « Servie » pour
 * toutes les étapes, et seule sa section (le nom de l'étape) le distingue.
 *
 * Indexé avec un événement vide, comme `labelOf` : les clés engendrées par typologie ou
 * par instance n'y sont pas, et la note se contente alors de les nommer.
 */
export function placeOf(key: string): string {
  homes ??= new Map(
    indexCatalog({ typos: [], forms: [] }).map((e) => [e.setting.key, e]),
  );
  const home = homes.get(key);
  if (!home) return "";
  return [home.screen, home.group, home.setting.section]
    .filter(Boolean)
    .join(" · ");
}

/** Sans accents ni majuscules : « règle » se trouve en tapant « regle ». */
export function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * Tout le catalogue, une entrée par clé.
 *
 * ⚠️ RÉSOLU AVEC LE CONTEXTE DE L'ÉVÉNEMENT : sans lui, les familles engendrées (une
 * ligne par typologie, les formulaires servis) ne produiraient rien, et la recherche
 * dirait « aucun réglage » sur ce que le panneau affiche.
 */
export function indexCatalog(context: CatalogContext): SearchEntry[] {
  const out: SearchEntry[] = [];
  const seen = new Set<string>();
  for (const [scope, groups] of Object.entries(CATALOG)) {
    for (const group of groups) {
      const settings =
        typeof group.settings === "function"
          ? group.settings(context)
          : group.settings;
      const groupNeeds =
        typeof group.needs === "function" ? group.needs(undefined) : group.needs;
      const manager = settings.find(
        (s) => s.type === "steps" || s.type === "pages",
      );
      for (const setting of settings) {
        if (seen.has(setting.key)) continue;
        seen.add(setting.key);
        /* Un nom de choix (`labelKey`) se dessine sous la liste qui le déclare : c'est
           elle qu'on désigne, comme le gestionnaire pour les lignes qu'il porte. */
        const owner = settings.find((s) =>
          s.options?.some((o) => o.labelKey === setting.key),
        );
        out.push({
          setting,
          scope,
          screen: screenName(scope),
          group: group.title,
          ...(groupNeeds ? { groupNeeds } : {}),
          siblings: settings,
          aimKey: setting.managed
            ? ((owner ?? manager)?.key ?? setting.key)
            : setting.key,
        });
      }
    }
  }
  return out;
}

/** Ce que la recherche sait de l'événement, au-delà de ses réglages. */
export type SearchEvent = {
  /** Le nom de la table `Events`, que le site montre quand le réglage est vide. */
  eventName?: string;
};

/**
 * Le texte qu'un réglage affiche sur le site, balises retirées.
 *
 * ⚠️ LE NOM DE L'ÉVÉNEMENT NON RÉGLÉ N'EST PAS « Événement » : le site montre celui de la
 * base, et c'est par lui qu'on doit retrouver la ligne (bug #285). Même règle que le site,
 * `eventNameOf`.
 */
function shownText(
  setting: Setting,
  read: (key: string) => unknown,
  event: SearchEvent,
): string {
  if (setting.type !== "text" && setting.type !== "html") return "";
  const raw = read(setting.key);
  const fallback = setting.fallback ?? fallbackOf(setting.key);
  if (setting.key === EVENT_NAME_KEY)
    return eventNameOf(raw, event.eventName) || fallback;
  const text = typeof raw === "string" && raw.trim() !== "" ? raw : fallback;
  return setting.type === "html"
    ? text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    : text;
}

/**
 * Pourquoi un réglage trouvé n'est pas à l'écran, s'il ne l'est pas.
 *
 * ⚠️ LA RECHERCHE LE DIT au lieu de le taire : un résultat qui ouvre une famille où la
 * ligne n'apparaît pas se lit comme une panne. On nomme l'interrupteur à allumer, et le
 * panneau désigne cet interrupteur plutôt que la ligne absente.
 */
function hiddenOf(
  entry: SearchEntry,
  read: (key: string) => unknown,
): Hidden | undefined {
  if (entry.groupNeeds && read(entry.groupNeeds) === false)
    return { by: "step" };
  if (entry.setting.managed) return undefined;
  const rendered = entry.siblings.filter((s) => !s.managed);
  const shown = visibleSettings(rendered, read);
  if (shown.includes(entry.setting)) return undefined;
  /* Le premier interrupteur de la famille, à l'écran, dont dépend le réglage. */
  const needs = Array.isArray(entry.setting.needs)
    ? entry.setting.needs
    : entry.setting.needs
      ? [entry.setting.needs]
      : [];
  for (const need of needs) {
    const negated = need.startsWith("!");
    const key = need.replace(/^!/, "");
    const master = shown.find((s) => s.key === key);
    if (master) return { by: "switch", key: master.key, label: master.label };
    /* Une clé que pilote un sélecteur n'est pas une ligne : on nomme le sélecteur et son
       option, et seulement si c'est bien cette condition qui ne tient pas.
       ⚠️ LE SÉLECTEUR PEUT VIVRE DANS UNE AUTRE FAMILLE (« Adresse inconnue » dort quand
       la connexion est désactivée, et le mode est réglé dans « Écran de connexion ») :
       « masqué par un autre réglage de sa famille » y était faux. On le nomme quand
       même, et le panneau désigne alors la ligne cherchée, faute de pouvoir viser
       une ligne d'une autre famille. */
    const driven = drivenOptionOf(key, read);
    if (driven && (read(key) === true) === negated) {
      const inFamily = shown.some((s) => s.key === driven.driver);
      const where = inFamily ? "" : placeOf(driven.driver);
      return {
        by: "mode",
        key: inFamily ? driven.driver : entry.aimKey,
        label: driven.label,
        option: driven.option,
        ...(where ? { where } : {}),
      };
    }
    if (driven) continue;
    /* ⚠️ UN INTERRUPTEUR D'UNE AUTRE FAMILLE (bug #294) : « Image de pied de page »
       dépend de « Afficher le pied de page », rangé dans « Pied de page ». On le nomme
       et on dit où il se règle, seulement si c'est bien lui qui ne tient pas. */
    if (entry.siblings.some((s) => s.key === key)) continue;
    const raw = read(key);
    const on = typeof raw === "boolean" ? raw : defaultOnOf(key);
    const label = labelOf(key);
    if (label && on === negated) {
      const where = placeOf(key);
      return {
        by: "switch",
        key: entry.aimKey,
        label,
        ...(where ? { where } : {}),
        ...(negated ? { negated: true as const } : {}),
      };
    }
  }
  return { by: "other" };
}

export function searchCatalog(
  entries: ReadonlyArray<SearchEntry>,
  query: string,
  read: (key: string) => unknown,
  event: SearchEvent = {},
): SearchHit[] {
  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const scored: Array<{ hit: SearchHit; score: number; order: number }> = [];
  entries.forEach((entry, order) => {
    const { setting } = entry;
    const label = normalize(`${setting.label} ${setting.section ?? ""}`);
    const shown = shownText(setting, read, event);
    const site = normalize(shown);
    const where = normalize(`${entry.group} ${entry.screen}`);
    const words = normalize((setting.keywords ?? []).join(" "));
    const has = (t: string) =>
      label.includes(t) ||
      site.includes(t) ||
      words.includes(t) ||
      where.includes(t);
    if (!tokens.every(has)) return;

    /* Le libellé d'abord, puis le texte du site ou les mots-clés, puis l'écran ou la
       famille seuls : « connexion » doit d'abord trouver les réglages qui en parlent,
       pas les seize lignes de l'écran du même nom. */
    const score = tokens.every((t) => label.includes(t))
      ? 0
      : tokens.every(
            (t) => label.includes(t) || site.includes(t) || words.includes(t),
          )
        ? 1
        : 2;
    const fromSite = tokens.some((t) => site.includes(t) && !label.includes(t));
    const hidden = hiddenOf(entry, read);
    scored.push({
      hit: {
        ...entry,
        ...(fromSite && shown ? { shown } : {}),
        ...(hidden ? { hidden } : {}),
      },
      score,
      order,
    });
  });

  const best = scored
    .sort((a, b) => a.score - b.score || a.order - b.order)
    .slice(0, MAX_HITS)
    .map((s) => s.hit);

  /* Rangés par écran, dans l'ordre où chaque écran apparaît pour la première fois :
     les meilleurs résultats restent en tête, et un écran n'a qu'un seul intertitre. */
  const screens = [...new Set(best.map((h) => h.scope))];
  return screens.flatMap((scope) => best.filter((h) => h.scope === scope));
}

/**
 * Découpe un texte autour du premier passage qui répond, pour le surligner.
 * Rien n'est surligné si la normalisation change la longueur (une ligature) : mieux
 * vaut pas de surlignage qu'un surlignage décalé.
 */
export function markOf(
  text: string,
  query: string,
): { before: string; match: string; after: string } | null {
  const flat = normalize(text);
  if (flat.length !== text.length) return null;
  for (const token of normalize(query).split(/\s+/).filter(Boolean)) {
    const at = flat.indexOf(token);
    if (at >= 0)
      return {
        before: text.slice(0, at),
        match: text.slice(at, at + token.length),
        after: text.slice(at + token.length),
      };
  }
  return null;
}
